import PolicySuccessIcon from '@svg/green-checkmark-circle.svg?react';
import FailureIcon from '@svg/x-circle-red.svg?react';
import React from 'react';
import moment, { Moment } from 'moment';
import { PolicyType } from '../utils/enums';

export enum NotificationResponseStatus {
    NOTIFICATION_SUCCESS = 'NOTIFICATION_SUCCESS',
    NOTIFICATION_ERROR = 'NOTIFICATION_ERROR',
    NOTIFICATION_PENDING = 'NOTIFICATION_PENDING',
    NOTIFICATION_PARTIAL_SUCCESS = 'NOTIFICATION_PARTIAL_SUCCESS'
}

export enum NotificationDownloadStatus {
    DOWNLOADING = 'DOWNLOADING',
    DOWNLOADED = 'DOWNLOADED',
    NOT_DOWNLOADED = 'NOT_DOWNLOADED'
}

export enum NotificationType {
    POLICY_UPDATE = 'policy',
    LOG_EXPORT = 'logs',
    TIPS = 'tips'
}

export interface NotificationDto {
    id: string;
    customerId?: number;
    status: NotificationResponseStatus;
    notificationTimestamp: Moment;
    type: NotificationType;
    serviceId: number;
    userId?: string;
    errorDescription?: string;
}

export interface PolicyNotificationDto extends NotificationDto {
    policyExternalId?: string;
    policyVersionNumber?: number;
    userName?: string;
    description?: string;
    pushPolicyTargetConnectionNameAndStatusList?: { connection: string; status: 'success' | 'failure' | null; }[];
    versionType?: PolicyType;
    isVersionDeleted?: boolean | null;
}

export interface LogNotificationDto extends NotificationDto {
    description?: string;
    fileName?: string;
    fileSize?: string;
}


export interface TipNotificationDto extends NotificationDto {
    policyVersionNumber?: number;
    tipNotificationType?: 'appRecommendation' | 'mlRecommendation';
}

export interface AppRecommendationTipNotificationDto extends TipNotificationDto {
    inboundRecommendedApps?: string[];
    outboundRecommendedApps?: string[];
}

export interface MlTipNotificationDto extends TipNotificationDto {
    applicationsToBlock?: string[];
    ipsToBlock?: string[];
    applied: boolean;
}

export interface NotificationResponseDto {
    id: string;
    serviceId: number;
    notificationType: NotificationType;
    notificationResponseStatus: NotificationResponseStatus;
    notificationTimestamp: string; // use ISO date format in TypeScript
    userId?: string;
    customerId?: number;
    userName?: string;
    policyVersionNumber?: number;
    policyExternalId?: string;
    fileName?: string;
    description?: string;
    errorDescription?: string;
    fileSize?: string;
    outboundRecommendedApps?: string[];
    inboundRecommendedApps?: string[];
    pushPolicyTargetConnectionNameAndStatusList?: { connection: string; status: 'success' | 'failure'; }[];
    versionType?: PolicyType;
    isVersionDeleted?: boolean | null;
    tipNotificationType?: 'appRecommendation' | 'mlRecommendation';
    applicationsToBlock?: string[];
    ipsToBlock?: string[];
    applied?: boolean;
}

const convertNotificationType = (type: string) => {
    switch (type) {
        case 'policy': return NotificationType.POLICY_UPDATE;
        case 'logs':   return NotificationType.LOG_EXPORT;
        case 'tips':   return NotificationType.TIPS;
        default: return NotificationType.POLICY_UPDATE;
    }
};

export const convertNotification = (
    notification: NotificationResponseDto
): PolicyNotificationDto | LogNotificationDto | TipNotificationDto | AppRecommendationTipNotificationDto | MlTipNotificationDto => {
    const baseNotification = {
        type: convertNotificationType(notification.notificationType),
        status: notification.notificationResponseStatus,
        notificationTimestamp: moment(notification.notificationTimestamp).utc(),
        userId: notification.userId,
        serviceId: notification.serviceId,
        customerId: notification.customerId,
        errorDescription: notification.errorDescription,
        id: notification.id
    };
    if (baseNotification.type === NotificationType.POLICY_UPDATE) {
        return {
            ...baseNotification,
            policyExternalId: notification.policyExternalId,
            policyVersionNumber: notification.policyVersionNumber,
            userName: notification.userName,
            description: notification.description,
            pushPolicyTargetConnectionNameAndStatusList: notification.pushPolicyTargetConnectionNameAndStatusList,
            versionType: notification.versionType,
            isVersionDeleted: notification.isVersionDeleted
        } as PolicyNotificationDto;
    } else if (baseNotification.type === NotificationType.TIPS) {
        // Determine tipNotificationType
        const tipType = notification.tipNotificationType;
        if (tipType === 'appRecommendation') {
            return {
                ...baseNotification,
                policyVersionNumber: notification.policyVersionNumber,
                tipNotificationType: tipType,
                inboundRecommendedApps: notification.inboundRecommendedApps,
                outboundRecommendedApps: notification.outboundRecommendedApps
            } as AppRecommendationTipNotificationDto;
        } else if (tipType === 'mlRecommendation') {
            return {
                ...baseNotification,
                policyVersionNumber: notification.policyVersionNumber,
                tipNotificationType: tipType,
                applicationsToBlock: notification.applicationsToBlock,
                ipsToBlock: notification.ipsToBlock,
                applied: notification.applied
            } as MlTipNotificationDto;
        }
        return {
            ...baseNotification,
            policyVersionNumber: notification.policyVersionNumber,
            tipNotificationType: tipType
        } as TipNotificationDto;
    }
    return {
        ...baseNotification,
        description: notification.description,
        fileName: notification.fileName,
        fileSize: notification.fileSize
    } as LogNotificationDto;
};

export const notificationMessages: {
    [key in Exclude<NotificationType, NotificationType.TIPS>]: {
        [innerKey in NotificationResponseStatus]: {
            message: string;
            StatusIconComp?: React.FC;
        };
    };
} = {
    [NotificationType.LOG_EXPORT]: {
        [NotificationResponseStatus.NOTIFICATION_SUCCESS]: {
            message: 'The logs you requested are ready for download.',
        },
        [NotificationResponseStatus.NOTIFICATION_ERROR]: {
            message: 'Your latest log request has failed.',
            StatusIconComp: FailureIcon,
        },
        [NotificationResponseStatus.NOTIFICATION_PENDING]: {
            message: 'We are preparing the logs you requested.\nYou’ll be notified once ready.'
        },
        [NotificationResponseStatus.NOTIFICATION_PARTIAL_SUCCESS]: {
            message: 'Your latest log request has completed with error.',
            StatusIconComp: FailureIcon,
        },
    },
    [NotificationType.POLICY_UPDATE]: {
        [NotificationResponseStatus.NOTIFICATION_SUCCESS]: {
            message: 'has been applied successfully and is now Active.',
            StatusIconComp: PolicySuccessIcon,
        },
        [NotificationResponseStatus.NOTIFICATION_ERROR]: {
            message: 'has failed to be applied.',
            StatusIconComp: FailureIcon,
        },
        [NotificationResponseStatus.NOTIFICATION_PENDING]: {
            message: 'has been submitted and is now being processed.\nYou’ll be notified once applied.',
        },
        [NotificationResponseStatus.NOTIFICATION_PARTIAL_SUCCESS]: {
            message: '',
        },
    },
};
